import React, { useMemo, useCallback } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

const TEXT_FIELDS = ['marka', 'ay', 'bayiAdi', 'bayiKodu', 'adet'];

const VehicleServiceReportTable = ({ items, totalCount, loading, lazyParams, onPage, activeFilters, onFiltersChange, sortField, sortOrder, onSortChange }) => {
    const { th } = useLanguage();
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();

    const isDealer = hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole();
    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const tableFilters = useMemo(
        () => ({
            marka: { value: activeFilters?.marka ?? null, matchMode: activeFilters?.markaMatchMode ?? FilterMatchMode.CONTAINS },
            ay: { value: activeFilters?.ay ?? null, matchMode: activeFilters?.ayMatchMode ?? FilterMatchMode.CONTAINS },
            adet: { value: activeFilters?.adet ?? null, matchMode: activeFilters?.adetMatchMode ?? FilterMatchMode.CONTAINS },
            bayiAdi: { value: activeFilters?.bayiAdi ?? null, matchMode: activeFilters?.bayiAdiMatchMode ?? FilterMatchMode.CONTAINS },
            bayiKodu: { value: activeFilters?.bayiKodu ?? null, matchMode: activeFilters?.bayiKoduMatchMode ?? FilterMatchMode.CONTAINS }
        }),
        [activeFilters]
    );

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    const createTextColumn = (field, header, options = {}) => {
        const { body, sortable = true, minWidth = '150px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header,
            style: { minWidth },
            body,
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const tableItems = (items ?? []).map((item, index) => ({
        ...item,
        rowKey: `${item.marka}-${item.ay}-${index}`
    }));

    const columns = [
        createTextColumn('marka', th('vehicleServiceReport.columns.marka'), { minWidth: '140px' }),
        createTextColumn('ay', th('vehicleServiceReport.columns.ay'), { minWidth: '120px' }),
        createTextColumn('adet', th('vehicleServiceReport.columns.adet'), { minWidth: '120px' }),
        ...(!isDealer
            ? [
                  createTextColumn('bayiAdi', th('vehicleServiceReport.columns.bayiAdi'), { minWidth: '200px' }),
                  createTextColumn('bayiKodu', th('vehicleServiceReport.columns.bayiKodu'), { minWidth: '120px' })
              ]
            : [])
    ];

    return (
        <PreferencesDataTable
            tableKey="vehicle-service-entry-report"
            columns={columns}
            value={tableItems}
            totalRecords={totalCount}
            className="p-datatable-gridlines"
            showGridlines
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows || 20}
            onPage={onPage}
            dataKey="rowKey"
            loading={loading}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 20, 50, 100]}
            tableStyle={{ minWidth: '80rem' }}
            onSort={(e) => onSortChange?.(e)}
            sortField={sortField}
            sortOrder={sortOrder}
            sortMode="single"
        />
    );
};

export default VehicleServiceReportTable;
